import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { format } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faUsers, faCheckCircle, faSyncAlt, faInfoCircle, faSave, faArrowLeft } from '@fortawesome/free-solid-svg-icons';

import AuthContext from '../../contexts/AuthContext';
import AttendanceCard from '../../components/attendance/AttendanceCard';
import attendanceService from '../../services/attendanceService';
import halaqaService from '../../services/halaqaService';
import './AttendanceManagementPage.css';

const AttendanceManagementPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  
  // Extract params from location state or URL params
  const routeParams = location.state || {};

  // Component state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [halaqas, setHalaqas] = useState([]);
  const [selectedHalaqa, setSelectedHalaqa] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [students, setStudents] = useState([]);
  const [attendanceData, setAttendanceData] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showHalaqaSelector, setShowHalaqaSelector] = useState(false);

  // Initialize component with location state or fetch data
  useEffect(() => {
    const initializeScreen = async () => {
      try {
        setLoading(true);
        
        // Fetch teacher's halaqas
        console.log('Fetching teacher halaqas...');
        let teacherHalaqas = [];
        try {
          teacherHalaqas = await halaqaService.getAssignedHalaqas();
          console.log('Teacher halaqas fetched:', teacherHalaqas);
        } catch (halaqaError) {
          console.error('Error fetching halaqas:', halaqaError);
          alert('Failed to load halaqas.');
          teacherHalaqas = []; // Ensure we have an array even on error
        }
        
        if (teacherHalaqas.length === 0) {
          alert('You are not assigned to any classes.');
          navigate(-1);
          setLoading(false);
          return;
        }
        
        setHalaqas(teacherHalaqas);
        
        // Set selected halaqa (from route params or first halaqa)
        const initialHalaqa = routeParams.halaqaId
          ? teacherHalaqas.find(h => h._id === routeParams.halaqaId)
          : teacherHalaqas[0];
          
        console.log('Selected halaqa:', initialHalaqa);
        setSelectedHalaqa(initialHalaqa);
        
        // Set date (from route params or today)
        if (routeParams.date) {
          setSelectedDate(new Date(routeParams.date));
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error initializing screen:', error);
        alert('Failed to initialize the attendance screen.');
        setLoading(false);
        navigate(-1);
      }
    };
    
    initializeScreen();
  }, [routeParams.halaqaId, routeParams.date, navigate]);

  // Fetch students and attendance when selected halaqa or date changes
  useEffect(() => {
    if (!selectedHalaqa) return;
    
    const fetchStudentsAndAttendance = async () => {
      try {
        setLoading(true);
        
        // Fetch students for selected halaqa
        const studentsData = await halaqaService.getHalaqaStudents(selectedHalaqa._id);
        setStudents(studentsData);
        
        // Format date for API call
        const dateObject = new Date(selectedDate);
        dateObject.setHours(0, 0, 0, 0);
        const dateString = format(dateObject, 'yyyy-MM-dd');
        
        // Fetch attendance records for the selected date
        const attendanceRecords = await attendanceService.getAttendanceByHalaqaAndDate(
          selectedHalaqa._id,
          dateString
        );
        
        // Create attendance data object for easier lookup
        const attendanceMap = {};
        attendanceRecords.forEach(record => {
          attendanceMap[record.studentId._id] = record.status;
        });
        
        setAttendanceData(attendanceMap);
        setHasChanges(false);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching students or attendance:', error);
        alert('Failed to load students or attendance data.');
        setLoading(false);
      }
    };
    
    fetchStudentsAndAttendance();
  }, [selectedHalaqa, selectedDate]);

  // Update attendance status for a student
  const handleStatusChange = (studentId, status) => {
    setAttendanceData(prev => ({
      ...prev,
      [studentId]: status
    }));
    setHasChanges(true);
  };

  // Mark all students as present
  const markAllPresent = () => {
    const updatedAttendance = {};
    students.forEach(student => {
      updatedAttendance[student._id] = 'present';
    });
    setAttendanceData(updatedAttendance);
    setHasChanges(true);
  };

  // Reset all attendance statuses
  const resetAll = () => {
    setAttendanceData({});
    setHasChanges(true);
  };

  // Handle halaqa change
  const handleHalaqaChange = (halaqa) => {
    setSelectedHalaqa(halaqa);
    setAttendanceData({});
    setHasChanges(false);
    setShowHalaqaSelector(false);
  };

  // Save attendance data
  const saveAttendance = async () => {
    if (!hasChanges) {
      alert('No attendance changes to save.');
      return;
    }
    
    try {
      setSaving(true);
      
      // Format date for API
      const dateString = format(selectedDate, 'yyyy-MM-dd');
      
      // Create attendance records array for bulk update
      const attendanceRecords = Object.keys(attendanceData).map(studentId => ({
        studentId,
        status: attendanceData[studentId]
      }));
      
      // Save attendance data
      await attendanceService.markBulkAttendance(
        selectedHalaqa._id,
        attendanceRecords,
        dateString,
        user.center
      );
      
      setSaving(false);
      setHasChanges(false);
      
      alert('Attendance has been saved successfully.');
    } catch (error) {
      console.error('Error saving attendance:', error);
      alert('Failed to save attendance. Please try again.');
      setSaving(false);
    }
  };

  // Calculate attendance statistics
  const attendanceStats = useMemo(() => {
    if (!students.length) return { present: 0, absent: 0, late: 0, unmarked: 0, total: 0 };

    const stats = { present: 0, absent: 0, late: 0, unmarked: 0, total: students.length };

    students.forEach(student => {
      const status = attendanceData[student._id];
      if (status === 'present') stats.present++;
      else if (status === 'absent') stats.absent++;
      else if (status === 'late') stats.late++;
      else stats.unmarked++;
    });

    return stats;
  }, [students, attendanceData]);

  // Toggle halaqa selector
  const toggleHalaqaSelector = () => {
    setShowHalaqaSelector(!showHalaqaSelector);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading attendance...</p>
      </div>
    );
  }

  return (
    <div className="attendance-management-page">
      <div className="page-header">
        <h1 className="page-title">Attendance Management</h1>
        <Link to="/dashboard" className="back-link">
          <FontAwesomeIcon icon={faArrowLeft} className="back-icon" />
          Back to Dashboard
        </Link>
      </div>
      
      <div className="attendance-controls">
        {/* Date selector */}
        <div className="control-group">
          <label className="control-label">Date</label>
          <div className="date-selector">
            <FontAwesomeIcon icon={faCalendarAlt} className="selector-icon" />
            <DatePicker
              selected={selectedDate}
              onChange={date => setSelectedDate(date)}
              maxDate={new Date()}
              dateFormat="EEEE, MMMM d, yyyy"
              className="date-input"
            />
          </div>
        </div>

        {/* Halaqa selector */}
        <div className="control-group">
          <label className="control-label">Class</label>
          <div className="halaqa-selector">
            <div className="selector-display" onClick={toggleHalaqaSelector}>
              <FontAwesomeIcon icon={faUsers} className="selector-icon" />
              <span className="selector-text">
                {selectedHalaqa ? selectedHalaqa.name : 'Select Class'}
              </span>
            </div>
            
            {showHalaqaSelector && halaqas.length > 1 && (
              <div className="halaqa-dropdown">
                {halaqas.map(halaqa => (
                  <div 
                    key={halaqa._id} 
                    className={`halaqa-option ${selectedHalaqa && selectedHalaqa._id === halaqa._id ? 'active' : ''}`}
                    onClick={() => handleHalaqaChange(halaqa)}
                  >
                    {halaqa.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Attendance Stats - Row Layout */}
      <div className="attendance-stats-container">
        <div className="attendance-stats-row">
          <div className="stat-card present">
            <span className="stat-number">{attendanceStats.present}</span>
            <span className="stat-label">Present</span>
          </div>
          
          <div className="stat-card absent">
            <span className="stat-number">{attendanceStats.absent}</span>
            <span className="stat-label">Absent</span>
          </div>
          
          <div className="stat-card late">
            <span className="stat-number">{attendanceStats.late}</span>
            <span className="stat-label">Late</span>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="action-buttons">
        <button 
          className="action-button"
          onClick={markAllPresent}
        >
          <FontAwesomeIcon icon={faCheckCircle} className="button-icon" />
          Mark All Present
        </button>
        
        <button 
          className="action-button"
          onClick={resetAll}
        >
          <FontAwesomeIcon icon={faSyncAlt} className="button-icon" />
          Reset
        </button>
      </div>

      {/* Student list */}
      <div className="student-list-header">
        <h2 className="section-title">Students ({students.length})</h2>
        <p className="helper-text">Tap status to change</p>
      </div>

      <div className="students-list">
        {students.length > 0 ? (
          students.map(student => (
            <AttendanceCard
              key={student._id}
              student={student}
              status={attendanceData[student._id]}
              onStatusChange={handleStatusChange}
            />
          ))
        ) : (
          <div className="empty-container">
            <p className="empty-text">
              No students found in this class.
            </p>
          </div>
        )}
      </div>

      {/* Save bar - fixed at bottom - always visible when there are changes */}
      <div className={`save-bar ${hasChanges ? 'visible' : 'hidden'}`}>
        <div className="save-status">
          <FontAwesomeIcon icon={faInfoCircle} className="info-icon" />
          <span className="status-text">
            {attendanceStats.unmarked} students unmarked
          </span>
        </div>
        
        <button
          className="save-button"
          onClick={saveAttendance}
          disabled={saving || !hasChanges}
        >
          {saving ? (
            <div className="spinner-small"></div>
          ) : (
            <>
              <FontAwesomeIcon icon={faSave} className="button-icon" />
              <span>Save</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default AttendanceManagementPage;